import { SectionThemeProvider, Typography } from "@trustpilot/businessapp-patternlibrary";
import { ConstellationProvider } from "@trustpilot/constellation-components";
import { Text, useTranslations } from "ilenia";
import React, { createContext, useCallback } from "react";
import OutsideClickHandler from "react-outside-click-handler";
import { useLocation, useHistory } from "react-router-dom";

import { isChildOf, isTabletDevice } from "../../components/helpers";
import { NavClose, NavBack } from "../sidebar/icons";
import { PlanInfo } from "./components/plan";
import { PrimaryNav } from "./components/primaryNav";
import * as Icons from "./icons";
import { StyledStar } from "./styled";
import styles from "./styles/sideNav.module.scss";
import * as Types from "./types";
import { navigationContextInitValues, keyboardEnterOrSpaceClick, useIsSmall, navigationMobileState } from "./utils";

//Creating the navigation context to pass it to child elements
export const NavContext = createContext(navigationContextInitValues);

const Sidebar: React.FC<Types.SideNav> = ({
  isMenuExpandOnMobile,
  hasAppError,
  expandMenuOnMobile,
  showSubMenu,
  setShowSubMenu,
  tier,
  trial,
  locale,
  eligibility,
  setShowFreeTrialModal,
  autoRenew,
  setIsMenuExpandOnMobile,
  isCollapsed,
  setIsCollapsed,
  navItems,
  setNavItems,
  newActiveItem,
  menu,
  reviewInsightsVisible,
  fetchedShellData,
}) => {
  const { pathname } = useLocation();
  const history = useHistory();
  const isSmall = useIsSmall();

  const [translations] = useTranslations();

  const { activePrimary, activeSecondary, activeTertiary } = newActiveItem
    ? newActiveItem
    : { activePrimary: "", activeSecondary: "", activeTertiary: "" };

  const closeSubNav = useCallback(
    (ignoreCollapse = false) => {
      setShowSubMenu(false, ignoreCollapse);
      setNavItems({
        ...navItems,
        isOpenPrimarySubmenu: newActiveItem?.activePrimary,
        isOpenSecondarySubmenu: newActiveItem?.activeSecondary,
        isTertiarySubmenu: newActiveItem?.activeTertiary,
      });
    },
    [activePrimary, activeSecondary, activeTertiary, showSubMenu, pathname],
  );

  const handleNavigation = useCallback((menu: Types.SubItem, evt: Types.Event) => {
    evt.preventDefault();

    navigationMobileState(menu, hasAppError, history, isSmall, setIsMenuExpandOnMobile);
  }, []);

  const openSubMenu = useCallback((menu: Types.SubItem, evt: Types.Event) => {
    evt.stopPropagation();
    handleNavigation(menu, evt);
  }, []);

  const navContextValue = {
    expandMenuOnMobile: expandMenuOnMobile,
    openSubMenu: openSubMenu,
    showSubMenu: showSubMenu,
    setShowSubMenu: setShowSubMenu,
    navItems: navItems,
    setNavItems: setNavItems,
    closeSubNav: closeSubNav,
    reviewInsightsVisible,
  };

  React.useEffect(() => {
    setNavItems({
      ...navItems,
      isOpenPrimarySubmenu: newActiveItem?.activePrimary,
      isOpenSecondarySubmenu: newActiveItem?.activeSecondary,
      isTertiarySubmenu: newActiveItem?.activeTertiary,
    });
  }, [activePrimary, activeSecondary, activeTertiary]);

  const canShowPrimaryNav =
    showSubMenu &&
    typeof navItems.isOpenPrimarySubmenu !== "undefined" &&
    navItems.isOpenPrimarySubmenu !== "home" &&
    navItems.isOpenPrimarySubmenu !== "help";

  return (
    <>
      <div className={styles.sidebarOuter} id="sidebar">
        <OutsideClickHandler
          onOutsideClick={(e) => {
            let ignoreCollapseMainMenu = false;
            if (isChildOf(e.target, "SelectorButton") || isChildOf(e.target, "BusinessUnitSelector")) {
              ignoreCollapseMainMenu = true;
            }
            closeSubNav(ignoreCollapseMainMenu);
          }}
        >
          <SectionThemeProvider theme="dark-green" className={styles.sidebarOuter}>
            <nav
              role="presentation"
              className={`${styles.sidebar} ${isMenuExpandOnMobile ? `${styles.active}` : ""} ${
                isCollapsed ? `${styles.collapsed}` : ""
              }`}
              onClick={() => {
                if (!isTabletDevice()) {
                  return;
                }
                setIsCollapsed(!isCollapsed);
                if (showSubMenu) {
                  setShowSubMenu(false);
                }
              }}
            >
              <div className={styles.sidebarActionBar}>
                {canShowPrimaryNav && (
                  <span
                    onClick={() => closeSubNav()}
                    tabIndex={0}
                    role="button"
                    onKeyDown={(event) => {
                      keyboardEnterOrSpaceClick(event, () => {
                        closeSubNav();
                      });
                    }}
                    data-testid="secondary_back_button"
                    aria-label={translations["ButtonAriaLabels.returnToMainMenu"]}
                  >
                    <NavBack />
                  </span>
                )}

                {(!showSubMenu ||
                  navItems.isOpenPrimarySubmenu === "home" ||
                  navItems.isOpenPrimarySubmenu === "help" ||
                  !navItems.isOpenPrimarySubmenu) && (
                  <img src="https://cdn.trustpilot.net/brand-assets/4.3.0/logo-monochrome-black.svg" alt="Trustpilot" />
                )}
                {canShowPrimaryNav && (
                  <Typography variant="display-xs">
                    <Text id={`Sidebar.${navItems?.isOpenPrimarySubmenu}`} />
                  </Typography>
                )}
                <span
                  onClick={() => {
                    expandMenuOnMobile();
                    setShowSubMenu(false);
                  }}
                  tabIndex={0}
                  onKeyDown={(event) => {
                    keyboardEnterOrSpaceClick(event, () => {
                      expandMenuOnMobile();
                    });
                  }}
                  role="button"
                  data-testid="close_menu"
                  aria-label={translations["ButtonAriaLabels.closeMenu"]}
                >
                  <NavClose />
                </span>
              </div>

              <div
                className={`${styles.sidebarContainer} ${
                  !isSmall && canShowPrimaryNav ? `${styles.sidebarActiveSub}` : ""
                }`}
              >
                <div>
                  {!isCollapsed && isSmall && (
                    <div className={styles.sidebarLogo}>
                      <img src="https://cdn.trustpilot.net/brand-assets/4.3.0/logo-white.svg" alt="Trustpilot" />
                    </div>
                  )}
                  {isCollapsed && (
                    <StyledStar>
                      <Icons.TrustpilotStar />
                    </StyledStar>
                  )}
                  <ul>
                    <NavContext.Provider value={navContextValue}>
                      <PrimaryNav primaryMenu={menu} setIsMainMenuCollapsed={setIsCollapsed} />
                    </NavContext.Provider>
                  </ul>
                </div>

                {fetchedShellData && !isCollapsed && (
                  <ConstellationProvider locale={locale}>
                    <PlanInfo
                      tier={tier}
                      trial={trial}
                      eligibility={eligibility}
                      autoRenew={autoRenew}
                      setShowFreeTrialModal={setShowFreeTrialModal}
                    />
                  </ConstellationProvider>
                )}
              </div>
            </nav>
          </SectionThemeProvider>
        </OutsideClickHandler>
      </div>
    </>
  );
};

export default Sidebar;
